import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { WTBCard } from './WTBCard';
import { CatalogCard } from './CatalogCard';
import { LocationAutocomplete } from './LocationAutocomplete';
import { useLocation } from '../context/LocationContext';
import { useAuth } from '../context/AuthContext';
import { getRecentlyViewed } from '../utils/recentlyViewed';
import { trackEvent } from '../utils/tracking';
import {
  Search,
  MapPin,
  ChevronDown,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  Laptop,
  Sofa,
  Shirt,
  Car,
  Bike,
  Home,
  Trophy,
  BookOpen,
  Wrench,
  Package,
  Users,
  ShoppingBag,
  Sparkles,
  Navigation,
  Clock,
  History,
  RefreshCw,
  DollarSign,
  Tag,
  Eraser,
  MoreHorizontal,
  EyeOff,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { toast } from '../lib/toast';
import { useNewItemsPolling } from '../hooks/useNewItemsPolling';
import { useTileSize } from '../hooks/useTileSize';
import { TileSizeSlider } from './TileSizeSlider';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  electronics: Laptop,
  furniture: Sofa,
  clothing: Shirt,
  vehicles: Car,
  sports: Bike,
  home: Home,
  collectibles: Trophy,
  books: BookOpen,
  tools: Wrench,
  baby: Users,
  music: Sparkles,
  pets: Package,
  other: Package,
};

const RADIUS_OPTIONS = [2, 5, 10, 20, 30, 40, 50, 75, 100, 500];

const PRICE_PRESETS = [
  { label: 'Any', min: null, max: null },
  { label: 'Under $20', min: null, max: 20 },
  { label: 'Under $50', min: null, max: 50 },
  { label: 'Under $100', min: null, max: 100 },
  { label: 'Under $500', min: null, max: 500 },
  { label: '$500+', min: 500, max: null },
];

const SIDEBAR_COLLAPSED_KEY = 'v2_sidebar_collapsed';

/**
 * CatalogView v2 — left-sidebar shell. Multi-select categories, collapsible rail,
 * inline category tree when expanded, floating popover when collapsed. Mobile
 * still opens the full sidebar as a drawer from a row-1 filter button.
 */
export function CatalogView({
  lockedCategory = null,
  lockedSubcategory = null,
  lockedSubcategoryOptions = null,
  linkPrefix = null,
  extraParams = null,
  heroSlot = null,
  bannerSlot = null,
  emptyStateSlot = null,
  showFeedModes = true,
  showCategoryDropdown = true,
  showViewToggle = true,
  showPriceDropdown = true,
  defaultSortBy = null,
  sectionTitle = null,
  itemNoun = 'items',
} = {}) {
  const { location, setManualLocation, requestLocation } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [catalogItems, setCatalogItems] = useState([]);
  const [wtbListings, setWtbListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');

  // Multi-select categories (array of category IDs). Subcategory remains single and
  // is only meaningful when exactly one category is selected.
  const [selectedCategories, setSelectedCategories] = useState(() => {
    if (lockedCategory) return [lockedCategory];
    const raw = searchParams.get('category') || '';
    return raw ? raw.split(',').filter(Boolean) : [];
  });
  const [selectedSubcategory, setSelectedSubcategory] = useState(lockedSubcategory || searchParams.get('sub') || null);

  const [radius, setRadius] = useState(50);
  const [locationInput, setLocationInput] = useState(location.name);
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  const [viewMode, setViewMode] = useState(() => {
    if (!showViewToggle) return 'buy';
    const viewParam = searchParams.get('view');
    if (viewParam === 'wants') return 'wants';
    return 'buy';
  });

  const [feedMode, setFeedMode] = useState(searchParams.get('feed') || 'nearby');

  const [minPrice, setMinPrice] = useState(searchParams.get('min') != null && searchParams.get('min') !== '' ? Number(searchParams.get('min')) : null);
  const [maxPrice, setMaxPrice] = useState(searchParams.get('max') != null && searchParams.get('max') !== '' ? Number(searchParams.get('max')) : null);

  const handleLoadNewItems = useCallback(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { newCount, loadNewItems, resetCount } = useNewItemsPolling({
    feedMode: viewMode === 'buy' ? feedMode : 'off',
    onLoadNewItems: handleLoadNewItems,
  });

  const { tileSize, setTileSize, gridStyle, MIN_TILE_SIZE, MAX_TILE_SIZE } = useTileSize();

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);

  // Refs + position state for the floating category popover.
  // Using fixed positioning avoids the sidebar's overflow-clip stacking context.
  const categoryBtnRef = useRef(null);
  const [categoryPopoverPos, setCategoryPopoverPos] = useState({ top: 0, left: 0 });

  // UI state
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showRadiusDropdown, setShowRadiusDropdown] = useState(false);
  const [showPricePopover, setShowPricePopover] = useState(false);
  const [showCategoryPopover, setShowCategoryPopover] = useState(false); // floating modal for categories
  const [expandedCategory, setExpandedCategory] = useState(null); // per-category sub expansion
  const [suppressMenuItemId, setSuppressMenuItemId] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  };

  // When Free is active (maxPrice === 0) we surface that via the dedicated Free
  // button; the Price pill shows "Price" greyed out so both UIs aren't lit up.
  const isFreeActive = maxPrice === 0;
  const priceLabel = useMemo(() => {
    if (isFreeActive) return 'Price';
    if (minPrice == null && maxPrice == null) return 'Price';
    if (minPrice == null) return `Under $${maxPrice}`;
    if (maxPrice == null) return `$${minPrice}+`;
    return `$${minPrice}–$${maxPrice}`;
  }, [minPrice, maxPrice, isFreeActive]);

  const hasActiveFilters = useMemo(() => {
    if (searchQuery) return true;
    if (!lockedCategory && selectedCategories.length > 0) return true;
    if (!lockedSubcategory && selectedSubcategory) return true;
    if (minPrice != null || maxPrice != null) return true;
    if (feedMode !== 'nearby') return true;
    if (radius !== 50) return true;
    return false;
  }, [searchQuery, selectedCategories, selectedSubcategory, minPrice, maxPrice, feedMode, radius, lockedCategory, lockedSubcategory]);

  const [showRecent, setShowRecent] = useState(false);

  const clearAllFilters = useCallback(() => {
    setSearchQuery('');
    setSearchInput('');
    if (!lockedCategory) setSelectedCategories([]);
    if (!lockedSubcategory) setSelectedSubcategory(null);
    setMinPrice(null);
    setMaxPrice(null);
    setFeedMode('nearby');
    setRadius(50);
    setShowRecent(false);
  }, [lockedCategory, lockedSubcategory]);

  const extraParamsKey = useMemo(() => JSON.stringify(extraParams || {}), [extraParams]);

  useEffect(() => {
    if (!showViewToggle) return;
    const viewParam = searchParams.get('view');
    if (viewParam === 'wants') setViewMode('wants');
    else setViewMode('buy');
    setShowRecent(false);
  }, [searchParams, showViewToggle]);

  const toggleViewMode = () => {
    setViewMode(prev => (prev === 'wants' ? 'buy' : 'wants'));
    setShowRecent(false);
  };

  const filteredRecent = recentlyViewed.filter(item =>
    viewMode === 'buy' ? item.type === 'inventory' : item.type === 'wtb'
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-dropdown]')) {
        setShowLocationDropdown(false);
        setShowRadiusDropdown(false);
        setShowPricePopover(false);
        setShowCategoryPopover(false);
      }
      setSuppressMenuItemId(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Re-measure the category popover anchor on open, resize, and scroll so the
  // fixed-positioned popover stays next to the sidebar button.
  useEffect(() => {
    if (!showCategoryPopover) return;
    const reposition = () => {
      if (!categoryBtnRef.current) return;
      const rect = categoryBtnRef.current.getBoundingClientRect();
      setCategoryPopoverPos({ top: rect.top, left: rect.right + 8 });
    };
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [showCategoryPopover, sidebarCollapsed]);

  useEffect(() => {
    const recent = getRecentlyViewed();
    const filtered = user ? recent.filter(item => item.user_id !== user.id) : recent;
    setRecentlyViewed(filtered);
  }, [user]);

  // Serialize categories to a stable string for effect dependency
  const selectedCategoriesKey = useMemo(() => selectedCategories.join(','), [selectedCategories]);

  useEffect(() => {
    resetCount();
    fetchData();
  }, [location, selectedCategoriesKey, selectedSubcategory, radius, searchQuery, user?.id, viewMode, feedMode, minPrice, maxPrice, extraParamsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLocationInput(location.name);
  }, [location.name]);

  useEffect(() => {
    if (lockedCategory) setSelectedCategories([lockedCategory]);
    if (lockedSubcategory !== null && lockedSubcategory !== undefined) {
      setSelectedSubcategory(lockedSubcategory);
    }
  }, [lockedCategory, lockedSubcategory]);

  useEffect(() => {
    if (lockedCategory) return;
    const raw = searchParams.get('category') || '';
    setSelectedCategories(raw ? raw.split(',').filter(Boolean) : []);
    setSelectedSubcategory(searchParams.get('sub') || null);
    setFeedMode(searchParams.get('feed') || 'nearby');
    if (showViewToggle) setViewMode(searchParams.get('view') === 'wants' ? 'wants' : 'buy');
    const q = searchParams.get('q') || '';
    setSearchQuery(q);
    setSearchInput(q);
    const min = searchParams.get('min');
    const max = searchParams.get('max');
    setMinPrice(min !== null && min !== '' ? Number(min) : null);
    setMaxPrice(max !== null && max !== '' ? Number(max) : null);
  }, [searchParams, lockedCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lockedCategory) return;
    const params = new URLSearchParams();
    if (viewMode === 'wants') params.set('view', 'wants');
    if (selectedCategories.length) params.set('category', selectedCategories.join(','));
    if (selectedSubcategory) params.set('sub', selectedSubcategory);
    if (feedMode !== 'nearby') params.set('feed', feedMode);
    if (searchQuery) params.set('q', searchQuery);
    if (minPrice != null) params.set('min', String(minPrice));
    if (maxPrice != null) params.set('max', String(maxPrice));
    setSearchParams(params, { replace: true });
  }, [selectedCategoriesKey, selectedSubcategory, viewMode, feedMode, searchQuery, minPrice, maxPrice, lockedCategory, setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const getCatalogParams = useCallback(() => {
    const effectiveCategory = lockedCategory
      ? lockedCategory
      : selectedCategories.length
        ? selectedCategories.join(',')
        : undefined;
    const effectiveSubcategory = lockedSubcategory || selectedSubcategory || undefined;
    return {
      lat: location.lat,
      lng: location.lng,
      radius_km: radius,
      category: effectiveCategory,
      subcategory: effectiveSubcategory,
      search: searchQuery || undefined,
      feed_mode: feedMode,
      min_price: minPrice != null ? minPrice : undefined,
      max_price: maxPrice != null ? maxPrice : undefined,
      is_free: maxPrice === 0 ? true : undefined,
      sort_by: defaultSortBy || undefined,
      ...(extraParams || {}),
    };
  }, [location, radius, selectedCategories, selectedSubcategory, searchQuery, feedMode, minPrice, maxPrice, lockedCategory, lockedSubcategory, defaultSortBy, extraParams]);

  const fetchData = async () => {
    setLoading(true);
    setPage(0);
    setHasMore(true);
    try {
      const categoriesRes = await axios.get(`${API_URL}/api/categories`);
      setCategories(categoriesRes.data);

      if (viewMode === 'buy') {
        const headers = {};
        const token = localStorage.getItem('token');
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const catalogRes = await axios.get(`${API_URL}/api/inventory/catalog/`, {
          params: { ...getCatalogParams(), skip: 0, limit: 20 },
          headers,
        });
        setCatalogItems(catalogRes.data);
        if (catalogRes.data.length < 20) setHasMore(false);
      } else {
        const wtbCategory = lockedCategory
          ? lockedCategory
          : selectedCategories.length
            ? selectedCategories.join(',')
            : undefined;
        const listingsRes = await axios.get(`${API_URL}/api/wtb/`, {
          params: {
            lat: location.lat,
            lng: location.lng,
            radius_km: radius,
            category: wtbCategory,
            subcategory: lockedSubcategory || selectedSubcategory || undefined,
            search: searchQuery || undefined,
            exclude_user_id: user?.id || undefined,
            limit: 50,
          },
        });
        setWtbListings(listingsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || viewMode !== 'buy') return;
    setLoadingMore(true);
    const nextSkip = (page + 1) * 20;
    try {
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await axios.get(`${API_URL}/api/inventory/catalog/`, {
        params: { ...getCatalogParams(), skip: nextSkip, limit: 20 },
        headers,
      });
      if (res.data.length === 0) {
        setHasMore(false);
      } else {
        setCatalogItems(prev => [...prev, ...res.data]);
        setPage(prev => prev + 1);
        if (res.data.length < 20) setHasMore(false);
      }
    } catch (error) {
      console.error('Failed to load more:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, page, viewMode, getCatalogParams]);

  useEffect(() => {
    if (viewMode !== 'buy') return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading) loadMore();
      },
      { rootMargin: '300px' }
    );
    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [loadMore, viewMode, loading]);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setSearchQuery(searchInput);
      if (searchInput.trim()) {
        trackEvent('search', {
          query: searchInput.trim(),
          category: selectedCategories[0] || lockedCategory || undefined,
          subcategory: lockedSubcategory || selectedSubcategory || undefined,
        });
      }
    }
  };

  const handleUseMyLocation = () => {
    requestLocation();
    setShowLocationDropdown(false);
  };

  const handleSuppress = async (item, durationDays) => {
    setSuppressMenuItemId(null);
    const label = categories.find(c => c.id === item.category)?.name || item.category;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/users/suppress`,
        { category: item.category, subcategory: item.subcategory || null, label, duration_days: durationDays },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setCatalogItems(prev => prev.filter(i => i.category !== item.category));
      const msg = durationDays === null
        ? `${label} removed from your feed`
        : durationDays === 14
          ? `Hiding ${label} for 2 weeks`
          : durationDays === 30
            ? `Hiding ${label} for 1 month`
            : `Hiding ${label} for 3 months`;
      toast.success(msg);
    } catch {
      toast.error('Failed to update feed preferences');
    }
  };

  const currentItems = viewMode === 'buy' ? catalogItems : wtbListings;
  const itemCount = currentItems.length;

  const showCategoryControl = showCategoryDropdown && !lockedCategory && !lockedSubcategoryOptions;
  const showSubcategoryPills = lockedSubcategoryOptions && lockedSubcategoryOptions.length > 0;

  // Multi-select helpers
  const toggleCategorySelection = (catId) => {
    if (lockedCategory) return;
    setSelectedCategories(prev => {
      const exists = prev.includes(catId);
      const next = exists ? prev.filter(c => c !== catId) : [...prev, catId];
      if (next.length !== 1) setSelectedSubcategory(null);
      return next;
    });
  };
  const selectSubcategoryForCategory = (catId, subId) => {
    if (lockedCategory) return;
    setSelectedCategories([catId]);
    setSelectedSubcategory(subId);
  };
  const clearCategories = () => {
    if (lockedCategory) return;
    setSelectedCategories([]);
    setSelectedSubcategory(null);
  };

  // Chip rows: one per selected category. If exactly one cat + subcategory, chip label shows both.
  const categoryChips = useMemo(() => {
    if (!categories.length) return [];
    return selectedCategories.map(catId => {
      const cat = categories.find(c => c.id === catId);
      const isSingleWithSub = selectedCategories.length === 1 && selectedSubcategory;
      const sub = isSingleWithSub ? cat?.subcategories?.find(s => s.id === selectedSubcategory) : null;
      return {
        id: catId,
        label: sub ? `${cat?.name || catId} › ${sub.name}` : (cat?.name || catId),
        icon: CATEGORY_ICONS[catId] || Package,
      };
    });
  }, [selectedCategories, selectedSubcategory, categories]);

  const ForYouMenu = ({ item, open, onOpen, onClose }) => {
    const catLabel = categories.find(c => c.id === item.category)?.name || item.category;
    return (
      <div className="absolute top-1.5 right-1.5 z-10">
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); open ? onClose() : onOpen(); }}
          className="w-6 h-6 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
          title="Feed options"
          aria-label="Feed options"
        >
          <MoreHorizontal className="w-3.5 h-3.5" />
        </button>
        {open && (
          <div
            className="absolute right-0 top-7 w-52 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg shadow-xl py-1 text-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-1.5 text-xs font-semibold text-[var(--theme-text-muted)] uppercase tracking-wide flex items-center gap-1.5">
              <EyeOff className="w-3 h-3" /> Hide {catLabel}
            </div>
            {[
              { label: 'Snooze for 2 weeks', days: 14 },
              { label: 'Snooze for 1 month', days: 30 },
              { label: 'Snooze for 3 months', days: 90 },
              { label: `Don't show me ${catLabel}`, days: null },
            ].map(({ label, days }) => (
              <button
                key={label}
                onClick={() => handleSuppress(item, days)}
                className="w-full text-left px-3 py-2 hover:bg-[var(--theme-hover)] text-[var(--theme-text-primary)] transition-colors"
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const effectiveSectionTitle = sectionTitle || (
    showRecent
      ? 'Recently Viewed'
      : viewMode === 'buy'
        ? (feedMode === 'foryou' ? 'For You' : feedMode === 'recents' ? 'New Listings' : 'Items Near You')
        : 'What People Want Near You'
  );

  const sortOptions = [
    { id: 'foryou', label: 'Relevant', Icon: Sparkles },
    { id: 'nearby', label: 'Closest', Icon: Navigation },
    { id: 'recents', label: 'Recently Listed', Icon: Clock },
  ];

  // Category list — flat, click-to-toggle. No checkbox, no "All" header.
  // Long category names wrap to two lines via leading-tight + whitespace-normal.
  // `onClose` is called after an action that should dismiss the parent surface (floating modal only).
  const CategoryTree = ({ onClose = null, compact = false }) => (
    <div className="flex flex-col">
      {categories.map((cat) => {
        const Icon = CATEGORY_ICONS[cat.id] || Package;
        const hasSubs = cat.subcategories && cat.subcategories.length > 0;
        const isExpanded = expandedCategory === cat.id;
        const isSelected = selectedCategories.includes(cat.id);
        return (
          <div key={cat.id}>
            <div className={`flex items-stretch ${isSelected ? 'bg-[#C9956C]/10' : ''}`}>
              <button
                onClick={() => toggleCategorySelection(cat.id)}
                className={`flex-1 text-left ${compact ? 'px-2 py-1.5' : 'px-3 py-1.5'} hover:bg-[var(--theme-hover)] transition-colors flex items-start gap-2 leading-tight ${
                  isSelected ? 'text-[#C9956C] font-semibold' : 'text-[var(--theme-text-primary)]'
                }`}
                data-testid={`v2-cat-toggle-${cat.id}`}
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span className="flex-1 text-[11px] whitespace-normal break-words">{cat.name}</span>
              </button>
              {hasSubs && (
                <button
                  onClick={() => setExpandedCategory(isExpanded ? null : cat.id)}
                  className="px-1.5 text-[var(--theme-text-muted)] hover:text-[var(--theme-text-primary)] hover:bg-[var(--theme-hover)] flex items-center"
                  title={isExpanded ? 'Hide subcategories' : 'Show subcategories'}
                >
                  <ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                </button>
              )}
            </div>
            {hasSubs && isExpanded && (
              <div className="pl-5 bg-[var(--theme-hover)]/40">
                {cat.subcategories.map(sub => (
                  <button
                    key={sub.id}
                    onClick={() => { selectSubcategoryForCategory(cat.id, sub.id); if (onClose) onClose(); }}
                    className={`w-full text-left ${compact ? 'px-2 py-1' : 'px-3 py-1'} hover:bg-[var(--theme-hover)] transition-colors leading-tight text-[10.5px] whitespace-normal break-words ${
                      selectedCategories.length === 1 && selectedCategories[0] === cat.id && selectedSubcategory === sub.id
                        ? 'text-[#C9956C] font-semibold'
                        : 'text-[var(--theme-text-secondary)]'
                    }`}
                  >
                    {sub.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // Size helpers so collapsed/expanded share button classes.
  // Alignment: collapsed → icon centred; expanded → icon + text left-aligned.
  const btnBase = 'h-9 rounded-lg text-xs font-semibold border transition-colors flex items-center';

  // ── Sidebar body ─────────────────────────────────────────────────────────
  const SidebarBody = ({ inDrawer = false }) => {
    // Mobile drawer always renders expanded regardless of collapse state
    const isCollapsedLocal = inDrawer ? false : sidebarCollapsed;
    const itemText = (label) => (isCollapsedLocal ? null : <span className="truncate">{label}</span>);

    const closeMobileIfOpen = () => { if (mobileDrawerOpen) setMobileDrawerOpen(false); };

    return (
      <div className={`flex flex-col ${inDrawer ? 'p-4 gap-3' : isCollapsedLocal ? 'px-1 py-2 gap-2' : 'px-2 py-3 gap-3'}`}>
        {/* Sort group */}
        {showFeedModes && viewMode === 'buy' && (
          <div className="flex flex-col gap-1">
            {sortOptions.map(opt => {
              const Icon = opt.Icon;
              const active = feedMode === opt.id && !showRecent;
              return (
                <button
                  key={opt.id}
                  onClick={() => { setFeedMode(opt.id); setShowRecent(false); closeMobileIfOpen(); }}
                  className={`${btnBase} ${isCollapsedLocal ? 'px-0 justify-center' : 'px-2 gap-1.5 justify-start'} ${
                    active
                      ? 'bg-[#1E3A5F] border-[#1E3A5F] text-white'
                      : 'bg-[var(--theme-card)] border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:border-[var(--theme-border-hover)]'
                  }`}
                  title={opt.label}
                  data-testid={`v2-sort-${opt.id}`}
                >
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                  {itemText(opt.label)}
                </button>
              );
            })}
          </div>
        )}

        {/* Price group */}
        {showPriceDropdown && (
          <div className="flex flex-col gap-1" data-dropdown>
            <button
              onClick={(e) => {
                if (isFreeActive) return;
                e.stopPropagation();
                setShowPricePopover(v => !v);
                setShowCategoryPopover(false);
              }}
              disabled={isFreeActive}
              className={`${btnBase} ${isCollapsedLocal ? 'px-0 justify-center' : 'px-2 gap-1.5 justify-start'} ${
                isFreeActive
                  ? 'bg-[var(--theme-card)] border-[var(--theme-border)] text-[var(--theme-text-muted)] opacity-50 cursor-not-allowed'
                  : (minPrice != null || maxPrice != null)
                    ? 'bg-[#C9956C] border-[#C9956C] text-white'
                    : 'bg-[var(--theme-card)] border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:border-[var(--theme-border-hover)]'
              }`}
              title={isFreeActive ? 'Price disabled while Free is active' : `Price: ${priceLabel}`}
              data-testid="v2-price-btn"
            >
              <DollarSign className="w-3.5 h-3.5 flex-shrink-0" />
              {itemText(priceLabel)}
            </button>
            {/* Dedicated Free quick-pick — click to toggle on/off */}
            <button
              onClick={() => {
                if (isFreeActive) {
                  setMaxPrice(null);
                } else {
                  setMinPrice(null);
                  setMaxPrice(0);
                }
                closeMobileIfOpen();
              }}
              className={`${btnBase} ${isCollapsedLocal ? 'px-0 justify-center' : 'px-2 gap-1.5 justify-start'} ${
                isFreeActive
                  ? 'bg-[#059669] border-[#059669] text-white'
                  : 'bg-[var(--theme-card)] border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:border-[var(--theme-border-hover)]'
              }`}
              title={isFreeActive ? 'Click to turn off Free filter' : 'Show only free items'}
              data-testid="v2-free-btn"
            >
              <Tag className="w-3.5 h-3.5 flex-shrink-0" />
              {itemText('Free')}
            </button>
            {showPricePopover && (
              <div
                className={`${inDrawer ? 'static mt-1 w-full min-w-0' : 'absolute left-full ml-2 top-0 z-[70] min-w-[240px]'} bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg shadow-xl p-3`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {PRICE_PRESETS.map((preset) => {
                    const active = minPrice === preset.min && maxPrice === preset.max;
                    return (
                      <button
                        key={preset.label}
                        onClick={() => {
                          setMinPrice(preset.min);
                          setMaxPrice(preset.max);
                          setShowPricePopover(false);
                          closeMobileIfOpen();
                        }}
                        className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                          active
                            ? 'bg-[#C9956C] border-[#C9956C] text-white font-semibold'
                            : 'bg-transparent border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:border-[var(--theme-border-hover)]'
                        }`}
                      >
                        {preset.label}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    placeholder="Min"
                    value={minPrice ?? ''}
                    onChange={(e) => setMinPrice(e.target.value === '' ? null : Number(e.target.value))}
                    className="h-8 text-xs"
                  />
                  <span className="text-xs text-[var(--theme-text-muted)]">to</span>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Max"
                    value={maxPrice ?? ''}
                    onChange={(e) => setMaxPrice(e.target.value === '' ? null : Number(e.target.value))}
                    className="h-8 text-xs"
                  />
                </div>
                {(minPrice != null || maxPrice != null) && (
                  <button
                    onClick={() => { setMinPrice(null); setMaxPrice(null); setShowPricePopover(false); }}
                    className="mt-2 w-full text-xs text-[var(--theme-text-muted)] hover:text-[var(--theme-text-primary)] transition-colors"
                  >
                    Clear price filter
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Recently viewed — own row */}
        {filteredRecent.length > 0 && (
          <button
            onClick={() => { setShowRecent(prev => !prev); closeMobileIfOpen(); }}
            className={`${btnBase} ${isCollapsedLocal ? 'px-0 justify-center' : 'px-2 gap-1.5 justify-start'} ${
              showRecent
                ? 'bg-[#1E3A5F] border-[#1E3A5F] text-white'
                : 'bg-[var(--theme-card)] border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:border-[var(--theme-border-hover)]'
            }`}
            title={showRecent ? 'Back to catalog' : 'Recently Viewed'}
            data-testid="v2-recent-view-toggle"
          >
            <History className="w-3.5 h-3.5 flex-shrink-0" />
            {itemText('Recently Viewed')}
          </button>
        )}

        {/* Locked subcategory pills */}
        {showSubcategoryPills && (
          <div className="flex flex-col gap-1">
            {!isCollapsedLocal && (
              <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--theme-text-muted)] px-0.5">Type</div>
            )}
            {lockedSubcategoryOptions.map((opt) => {
              const active = (opt.id === null && !selectedSubcategory) || selectedSubcategory === opt.id;
              return (
                <button
                  key={opt.id || 'all'}
                  onClick={() => { setSelectedSubcategory(opt.id); closeMobileIfOpen(); }}
                  className={`${btnBase} ${isCollapsedLocal ? 'px-0 justify-center' : 'px-2 justify-start'} ${
                    active
                      ? 'bg-[#1E3A5F] border-[#1E3A5F] text-white'
                      : 'bg-[var(--theme-card)] border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:border-[var(--theme-border-hover)]'
                  }`}
                  title={opt.label}
                  data-testid={`v2-subcategory-pill-${opt.id || 'all'}`}
                >
                  {isCollapsedLocal ? opt.label.charAt(0) : opt.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Categories — always a floating modal so the chips stay pinned under the button */}
        {showCategoryControl && (
          <div className="relative flex flex-col gap-1" data-dropdown>
            <button
              ref={inDrawer ? null : categoryBtnRef}
              onClick={(e) => {
                e.stopPropagation();
                setShowCategoryPopover(v => !v);
                setShowPricePopover(false);
              }}
              className={`${btnBase} ${isCollapsedLocal ? 'px-0 justify-center' : 'px-2 gap-1.5 justify-start'} ${
                selectedCategories.length > 0
                  ? 'bg-[#C9956C] border-[#C9956C] text-white'
                  : 'bg-[var(--theme-card)] border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:border-[var(--theme-border-hover)]'
              }`}
              data-testid="v2-category-btn"
              title="Categories"
            >
              <Package className="w-3.5 h-3.5 flex-shrink-0" />
              {!isCollapsedLocal && (
                <>
                  <span className="truncate">Categories</span>
                  {selectedCategories.length > 1 && (
                    <span className="ml-auto text-[10px] bg-white/25 rounded-full px-1.5 py-0.5 leading-none">
                      {selectedCategories.length}
                    </span>
                  )}
                </>
              )}
            </button>

            {/* Selection chips — sit directly under the Categories button, never displaced */}
            {categoryChips.length > 0 && !isCollapsedLocal && (
              <div className="flex flex-col gap-0.5">
                {categoryChips.map(chip => (
                  <button
                    key={chip.id}
                    onClick={() => toggleCategorySelection(chip.id)}
                    className="group flex items-center gap-1 px-1.5 py-1 text-xs text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] hover:bg-[var(--theme-hover)] rounded-md transition-colors"
                    title={`Remove ${chip.label}`}
                  >
                    <X className="w-3 h-3 flex-shrink-0 group-hover:text-red-500" />
                    <span className="truncate">{chip.label}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Floating modal — fixed positioning escapes the sidebar's scroll container.
                In the mobile drawer we render inline below the button instead. */}
            {showCategoryPopover && inDrawer && (
              <div
                className="mt-1 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg shadow-xl min-w-[220px] max-h-[60vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <CategoryTree onClose={() => setShowCategoryPopover(false)} />
              </div>
            )}
          </div>
        )}

        {/* Clear all */}
        {hasActiveFilters && (
          <button
            onClick={() => { clearAllFilters(); closeMobileIfOpen(); }}
            className={`h-8 rounded-lg text-xs font-medium border border-[var(--theme-border)] bg-transparent text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] hover:border-[var(--theme-border-hover)] transition-colors flex items-center gap-1.5 ${isCollapsedLocal ? 'px-0 justify-center' : 'px-2 justify-start'}`}
            title="Clear all filters"
            data-testid="v2-clear-all"
          >
            <Eraser className="w-3 h-3 flex-shrink-0" />
            {!isCollapsedLocal && 'Clear all'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      {heroSlot}

      {/* Sticky Row 1: Search + Location + Radius (+ mobile Filter button) */}
      <div className="sticky top-[60px] md:top-[64px] z-40 bg-[var(--theme-catalog-bar)] -mt-px">
        <div className="flex items-center gap-2 px-3 md:px-4 py-2">
          <button
            onClick={() => setMobileDrawerOpen(true)}
            className="md:hidden flex-shrink-0 relative w-9 h-9 rounded-lg bg-[var(--theme-card)] border border-[var(--theme-border)] hover:border-[var(--theme-border-hover)] flex items-center justify-center transition-colors"
            title="Filters"
            data-testid="v2-mobile-filter-btn"
          >
            <SlidersHorizontal className="w-4 h-4 text-[var(--theme-text-primary)]" />
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#C9956C] border-2 border-[var(--theme-catalog-bar)]" />
            )}
          </button>

          {/* N — Buy/Wants toggle, sits to the left of search */}
          {showViewToggle && (
            <button
              onClick={toggleViewMode}
              className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 ${
                viewMode === 'buy'
                  ? 'bg-white border border-gray-200'
                  : 'bg-[#1E3A5F] border border-white/30'
              }`}
              data-testid="v2-view-mode-toggle"
              title={viewMode === 'buy' ? 'Viewing Buy Catalog — click for Wants' : 'Viewing Wants — click for Buy Catalog'}
            >
              <img
                src={viewMode === 'buy' ? '/n-toggle-buy.png' : '/n-toggle-wants.png'}
                alt={viewMode === 'wants' ? 'Wants' : 'Buy'}
                className="w-5 h-5 object-contain"
              />
            </button>
          )}

          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--theme-text-muted)]" />
            <Input
              type="text"
              placeholder={viewMode === 'buy' ? `Search ${itemNoun} for sale...` : 'Search wants...'}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="pl-8 h-9 bg-[var(--theme-card)] border-[var(--theme-border)] text-[var(--theme-text-primary)] rounded-lg text-sm placeholder:text-[var(--theme-text-muted)] w-full"
              data-testid="search-input"
            />
          </div>

          <div className="relative flex-shrink-0" data-dropdown>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowLocationDropdown(!showLocationDropdown);
                setShowRadiusDropdown(false);
              }}
              className="flex items-center gap-1.5 h-9 px-2.5 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg hover:border-[var(--theme-border-hover)] transition-colors"
              data-testid="location-dropdown-btn"
            >
              <MapPin className="w-3.5 h-3.5 text-[#C9956C]" />
              <span className="text-sm text-[var(--theme-text-primary)] truncate max-w-[80px] hidden sm:inline">{location.name}</span>
              <ChevronDown className={`w-3 h-3 text-[var(--theme-text-secondary)] transition-transform ${showLocationDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showLocationDropdown && (
              <div className="absolute top-full right-0 mt-1 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg shadow-lg z-[70] p-2 min-w-[220px]">
                <LocationAutocomplete
                  value={locationInput}
                  onChange={(val) => setLocationInput(val)}
                  onSelect={(locationData) => {
                    setManualLocation(locationData.lat, locationData.lng, locationData.name);
                    setShowLocationDropdown(false);
                  }}
                  placeholder="Enter suburb or postcode..."
                  className="mb-2"
                />
                <button
                  type="button"
                  onClick={() => { handleUseMyLocation(); setShowLocationDropdown(false); }}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[var(--theme-hover)] text-xs text-[var(--theme-text-primary)] transition-colors"
                >
                  <Navigation className="w-3 h-3 text-[#C9956C]" />
                  <span>Use my location</span>
                </button>
              </div>
            )}
          </div>

          <div className="relative flex-shrink-0" data-dropdown>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowRadiusDropdown(!showRadiusDropdown);
                setShowLocationDropdown(false);
              }}
              className="flex items-center gap-1 h-9 px-2.5 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg hover:border-[var(--theme-border-hover)] transition-colors"
              data-testid="radius-dropdown-btn"
            >
              <span className="text-sm text-[var(--theme-text-primary)]">{radius}km</span>
              <ChevronDown className={`w-3 h-3 text-[var(--theme-text-secondary)] transition-transform ${showRadiusDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showRadiusDropdown && (
              <div className="absolute top-full right-0 mt-1 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg shadow-lg z-[70] py-1 min-w-[90px]">
                {RADIUS_OPTIONS.map((r) => (
                  <button
                    key={r}
                    onClick={() => { setRadius(r); setShowRadiusDropdown(false); }}
                    className={`w-full text-left px-3 py-1 text-sm hover:bg-[var(--theme-hover)] transition-colors ${
                      radius === r ? 'text-[#C9956C] font-semibold bg-[var(--theme-hover)]' : 'text-[var(--theme-text-primary)]'
                    }`}
                  >
                    {r} km
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {bannerSlot}

      {newCount > 0 && viewMode === 'buy' && feedMode === 'recents' && (
        <div className="mx-3 md:mx-4 mt-2 animate-slide-down" style={{ animation: 'slideDown 0.25s ease-out' }}>
          <button
            onClick={loadNewItems}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1E3A5F] text-white text-sm font-semibold rounded-xl shadow-[0_4px_16px_rgba(30,58,95,0.35)] hover:bg-[#2D4A6F] active:scale-[0.98] transition-all duration-150"
            data-testid="new-items-banner"
          >
            <RefreshCw className="w-4 h-4" />
            {newCount === 1 ? '1 new item' : `${newCount} new items`} — Tap to see
          </button>
        </div>
      )}

      {/* Desktop category popover — fixed-positioned so it escapes the sidebar's
          overflow/scroll context. Fixed width so content (chevrons, long sub names)
          doesn't stretch the modal horizontally. */}
      {showCategoryPopover && (
        <div
          className="hidden md:block fixed z-[70] w-56 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg shadow-xl max-h-[60vh] overflow-y-auto"
          style={{ top: categoryPopoverPos.top, left: categoryPopoverPos.left }}
          data-dropdown
          onClick={(e) => e.stopPropagation()}
        >
          <CategoryTree onClose={() => setShowCategoryPopover(false)} />
        </div>
      )}

      <div className="flex">
        {/* Desktop sidebar rail — z-30 keeps popovers above the grid.
            Column flex so the collapse button pins to the very bottom. */}
        <aside
          className={`hidden md:flex flex-col flex-shrink-0 border-r border-[var(--theme-border)] sticky top-[116px] self-start h-[calc(100vh-116px)] overflow-visible transition-[width] duration-200 z-30 ${sidebarCollapsed ? 'w-14' : 'w-36'}`}
          data-testid="v2-sidebar"
        >
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-visible">
            <SidebarBody />
          </div>
          <button
            onClick={toggleSidebar}
            className={`flex-shrink-0 border-t border-[var(--theme-border)] h-9 bg-[var(--theme-card)] hover:bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] transition-colors flex items-center justify-center gap-1.5 ${sidebarCollapsed ? 'px-0' : 'px-2'}`}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-testid="v2-sidebar-toggle"
          >
            {sidebarCollapsed ? (
              <PanelLeftOpen className="w-4 h-4" />
            ) : (
              <>
                <PanelLeftClose className="w-4 h-4" />
                <span className="text-xs">Collapse</span>
              </>
            )}
          </button>
        </aside>

        {/* Mobile drawer */}
        {mobileDrawerOpen && (
          <div className="md:hidden fixed inset-0 z-[60] bg-black/50" onClick={() => setMobileDrawerOpen(false)}>
            <div
              className="absolute inset-y-0 left-0 w-[50%] max-w-[200px] bg-[var(--theme-bg)] shadow-2xl overflow-y-auto overflow-x-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-[var(--theme-bg)] border-b border-[var(--theme-border)] px-4 py-3 flex items-center justify-between z-10">
                <h3 className="text-sm font-bold text-[var(--theme-text-primary)]">Filters</h3>
                <button
                  onClick={() => setMobileDrawerOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-[var(--theme-hover)] transition-colors"
                  data-testid="v2-mobile-drawer-close"
                >
                  <X className="w-4 h-4 text-[var(--theme-text-primary)]" />
                </button>
              </div>
              <SidebarBody inDrawer />
            </div>
          </div>
        )}

        <div className="flex-1 min-w-0 px-3 md:px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-bold text-[var(--theme-text-primary)]">
                {effectiveSectionTitle}
              </h2>
              {showRecent ? (
                <p className="text-xs text-[var(--theme-text-secondary)]">
                  <strong className="text-[var(--theme-text-primary)]">{filteredRecent.length}</strong> {viewMode === 'buy' ? itemNoun : 'wants'} you've browsed
                </p>
              ) : (
                <p className="text-xs text-[var(--theme-text-secondary)]">
                  <strong className="text-[var(--theme-text-primary)]">{itemCount}</strong> {viewMode === 'buy' ? itemNoun : 'wants'} within {radius} km
                </p>
              )}
            </div>

            <div className="flex items-center gap-3">
              <TileSizeSlider tileSize={tileSize} setTileSize={setTileSize} min={MIN_TILE_SIZE} max={MAX_TILE_SIZE} />
              <div className="flex items-center gap-1.5 text-xs text-[var(--theme-text-muted)]">
                <span className="hidden sm:inline">Viewing:</span>
                <span className={`px-2 py-0.5 rounded-full font-medium ${
                  viewMode === 'buy' ? 'bg-[#1E3A5F] text-white' : 'bg-[#C9956C] text-white'
                }`}>
                  {showRecent ? 'Recent' : viewMode === 'buy' ? 'Buy' : 'Wants'}
                </span>
              </div>
            </div>
          </div>

          {showRecent ? (
            <div style={gridStyle} data-testid="recent-grid">
              {filteredRecent.map((item) => {
                if (item.type === 'inventory') {
                  return <CatalogCard key={item.id} item={item} linkPrefix={linkPrefix} />;
                }
                return <WTBCard key={item.id} listing={item} onClick={() => navigate(`/wtb/${item.id}`)} />;
              })}
            </div>
          ) : viewMode === 'buy' ? (
            <>
              {loading ? (
                <div style={gridStyle}>
                  {[...Array(12)].map((_, i) => (
                    <div key={i} className="animate-pulse bg-[var(--theme-card)] rounded-xl overflow-hidden border border-[var(--theme-border)]">
                      <div className="aspect-square bg-[var(--theme-hover)]" />
                      <div className="p-2 space-y-1.5">
                        <div className="h-3 bg-[var(--theme-hover)] rounded w-1/2" />
                        <div className="h-2.5 bg-[var(--theme-hover)] rounded w-3/4" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : catalogItems.length === 0 ? (
                emptyStateSlot || (
                  <div className="text-center py-12 bg-[var(--theme-card)] rounded-xl border border-[var(--theme-border)]">
                    <Search className="w-10 h-10 text-[var(--theme-text-muted)] mx-auto mb-3" />
                    <h3 className="text-base font-semibold text-[var(--theme-text-primary)] mb-1">No {itemNoun} found</h3>
                    <p className="text-sm text-[var(--theme-text-secondary)] mb-3">
                      {feedMode === 'foryou' && !searchQuery
                        ? 'Create a Want to personalize your feed'
                        : feedMode === 'recents' && !searchQuery
                          ? 'No recent listings in this area'
                          : searchQuery ? `No results for "${searchQuery}"` : `No ${itemNoun} in this category yet`}
                    </p>
                    {user && feedMode === 'foryou' && !searchQuery && feedMode !== 'recents' ? (
                      <Link to="/create-wtb">
                        <Button size="sm" className="bg-[#C9956C] hover:bg-[#B8845B] text-white">
                          <ShoppingBag className="w-3.5 h-3.5 mr-1.5" />
                          Create a Want
                        </Button>
                      </Link>
                    ) : user && (
                      <Link to={
                        lockedCategory === 'vehicles' ? '/auto/cars/list'
                        : lockedCategory === 'live_animals' ? '/pets/list'
                        : '/list-item'
                      }>
                        <Button size="sm" className="bg-[#059669] hover:bg-[#047857] text-white">
                          <Tag className="w-3.5 h-3.5 mr-1.5" />
                          Be the first to {lockedCategory === 'vehicles' ? 'list' : lockedCategory === 'live_animals' ? 'rehome' : 'sell'}
                        </Button>
                      </Link>
                    )}
                  </div>
                )
              ) : (
                <>
                  <div style={gridStyle} data-testid="catalog-grid">
                    {catalogItems.map((item) => (
                      <div key={item.id} className="relative">
                        <CatalogCard item={item} linkPrefix={linkPrefix} />
                        {feedMode === 'foryou' && user && (
                          <ForYouMenu
                            item={item}
                            open={suppressMenuItemId === item.id}
                            onOpen={() => setSuppressMenuItemId(item.id)}
                            onClose={() => setSuppressMenuItemId(null)}
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  <div ref={sentinelRef} className="h-1" />
                  {loadingMore && (
                    <div className="flex justify-center py-6">
                      <div className="w-6 h-6 border-2 border-[#1E3A5F] border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  {!hasMore && catalogItems.length > 0 && (
                    <p className="text-center text-xs text-[var(--theme-text-muted)] py-4">
                      You've seen all {catalogItems.length} {itemNoun}
                    </p>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              {loading ? (
                <div style={gridStyle}>
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="animate-pulse bg-[var(--theme-card)] rounded-xl overflow-hidden border border-[var(--theme-border)]">
                      <div className="aspect-square bg-[var(--theme-hover)]" />
                      <div className="p-2 space-y-1.5">
                        <div className="h-3 bg-[var(--theme-hover)] rounded w-1/2" />
                        <div className="h-2.5 bg-[var(--theme-hover)] rounded w-3/4" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : wtbListings.length === 0 ? (
                <div className="text-center py-12 bg-[var(--theme-card)] rounded-xl border border-[var(--theme-border)]">
                  <ShoppingBag className="w-10 h-10 text-[var(--theme-text-muted)] mx-auto mb-3" />
                  <h3 className="text-base font-semibold text-[var(--theme-text-primary)] mb-1">No wants found</h3>
                  <p className="text-sm text-[var(--theme-text-secondary)] mb-3">No one is looking for items in this area yet</p>
                  {user && (
                    <Link to="/create-wtb">
                      <Button size="sm" className="bg-[#C9956C] hover:bg-[#B8845B] text-white">
                        <ShoppingBag className="w-3.5 h-3.5 mr-1.5" />
                        Post what you want
                      </Button>
                    </Link>
                  )}
                </div>
              ) : (
                <div style={gridStyle} data-testid="wtb-grid">
                  {wtbListings.map((listing) => (
                    <WTBCard key={listing.id} listing={listing} onClick={() => navigate(`/wtb/${listing.id}`)} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CatalogView;
